import { useEffect, useRef, useState } from "react";
import Breadcrumbs from "../../styles/components/Breadcrumbs";
import "../../styles/pages/content/_contact.scss";

const RECAPTCHA_SCRIPT_ID = "google-recaptcha-api";
const RECAPTCHA_ONLOAD = "__moozRecaptchaOnload";
const SUBJECT_LABELS = {
    general: "General enquiry",
    account: "Account help",
    recording: "Recording issue",
    billing: "Billing",
    feedback: "Feedback",
};

function encodeForm(data) {
    return new URLSearchParams(data).toString();
}

function SuccessBanner() {
    return (
        <div className="ct-success" role="status" aria-live="polite">
            <span className="ct-success__icon" aria-hidden="true" />
            <div className="ct-success__text">
                <strong className="ct-success__heading">Message sent</strong>
                <span className="ct-success__sub">We'll be in touch soon.</span>
            </div>
        </div>
    );
}

// ─── Main export ──────────────────────────────────────────────────────────────
export default function Contact() {
    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [formIsComplete, setFormIsComplete] = useState(false);
    const [captchaStatus, setCaptchaStatus] = useState(() =>
        process.env.REACT_APP_RECAPTCHA_SITE_KEY ? "loading" : "missing"
    );
    const formRef = useRef(null);
    const recaptchaHostRef = useRef(null);
    const recaptchaWidgetIdRef = useRef(null);
    const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

    useEffect(() => {
        if (!recaptchaSiteKey) {
            setCaptchaStatus("missing");
            return;
        }
        if (submitted) return;

        let cancelled = false;

        const renderWidget = () => {
            if (
                cancelled ||
                !recaptchaHostRef.current ||
                !window.grecaptcha?.render ||
                recaptchaWidgetIdRef.current !== null
            ) {
                return;
            }

            setCaptchaStatus("loading");
            recaptchaWidgetIdRef.current = window.grecaptcha.render(
                recaptchaHostRef.current,
                {
                    sitekey: recaptchaSiteKey,
                    callback: () => setCaptchaStatus("verified"),
                    "expired-callback": () => setCaptchaStatus("idle"),
                    "error-callback": () => setCaptchaStatus("error"),
                }
            );
            setCaptchaStatus("idle");
        };

        window[RECAPTCHA_ONLOAD] = renderWidget;

        if (window.grecaptcha?.render) {
            renderWidget();
        } else if (!document.getElementById(RECAPTCHA_SCRIPT_ID)) {
            const script = document.createElement("script");
            script.id = RECAPTCHA_SCRIPT_ID;
            script.src = `https://www.google.com/recaptcha/api.js?render=explicit&onload=${RECAPTCHA_ONLOAD}`;
            script.async = true;
            script.defer = true;
            script.onerror = () => {
                if (!cancelled) {
                    setCaptchaStatus("error");
                }
            };
            document.body.appendChild(script);
        } else {
            setCaptchaStatus("loading");
        }

        return () => {
            cancelled = true;
            if (window[RECAPTCHA_ONLOAD] === renderWidget) {
                delete window[RECAPTCHA_ONLOAD];
            }
        };
    }, [recaptchaSiteKey, submitted]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");

        if (!recaptchaSiteKey) {
            setError("reCAPTCHA is not configured.");
            return;
        }

        const captchaResponse =
            recaptchaWidgetIdRef.current !== null &&
            window.grecaptcha?.getResponse
                ? window.grecaptcha.getResponse(recaptchaWidgetIdRef.current)
                : "";

        if (!captchaResponse) {
            setCaptchaStatus("idle");
            setError("Please complete the CAPTCHA.");
            return;
        }

        setSubmitting(true);
        try {
            const form = e.currentTarget;
            const formData = new FormData(form);
            const payload = {};
            for (const [key, value] of formData.entries()) {
                payload[key] = value;
            }
            if (payload.subject) {
                payload.subject =
                    SUBJECT_LABELS[payload.subject] || String(payload.subject);
            }

            const res = await fetch("/", {
                method: "POST",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
                body: encodeForm(payload),
            });

            if (!res.ok) {
                throw new Error("Failed to send message. Please try again.");
            }

            window.scrollTo({ top: 0, behavior: "smooth" });
            setSubmitted(true);
            form.reset();
            setFormIsComplete(false);
        } catch (err) {
            setError(err?.message || "Failed to send message.");
            if (
                recaptchaWidgetIdRef.current !== null &&
                window.grecaptcha?.reset
            ) {
                window.grecaptcha.reset(recaptchaWidgetIdRef.current);
            }
            setCaptchaStatus("idle");
        } finally {
            setSubmitting(false);
        }
    };

    const handleChange = () => {
        if (submitted) setSubmitted(false);
        if (error) setError("");
        setFormIsComplete(Boolean(formRef.current?.checkValidity()));
    };

    return (
        <div className="ct-page" id="contact">
            <div className="ct-container">
                <div className="ct-hero">
                    <Breadcrumbs
                        items={[
                            { label: "Home", href: "/" },
                            { label: "Contact", href: "/contact" },
                        ]}
                    />
                    <h1 className="ct-hero__title">Tell us what you need</h1>
                    <p className="ct-hero__sub">
                        Whether you're exploring Mooz, stuck on a recording, or
                        need account help, this is the fastest way to reach us.
                    </p>
                </div>

                <div className="ct-card">
                    {submitted ? (
                        <SuccessBanner />
                    ) : (
                        <form
                            ref={formRef}
                            className="ct-form"
                            name="contact"
                            method="POST"
                            data-netlify="true"
                            data-netlify-recaptcha="true"
                            data-netlify-honeypot="bot-field"
                            onSubmit={handleSubmit}
                            onChange={handleChange}
                        >
                            <input
                                type="hidden"
                                name="form-name"
                                value="contact"
                            />

                            {/* Honeypot */}
                            <div
                                className="ct-form__field ct-form__field--hidden"
                                aria-hidden="true"
                            >
                                <label htmlFor="ct-bot">
                                    Don't fill this out
                                </label>
                                <input
                                    id="ct-bot"
                                    name="bot-field"
                                    type="text"
                                    tabIndex="-1"
                                    autoComplete="off"
                                />
                            </div>

                            <div className="ct-form__row">
                                <div className="ct-form__field">
                                    <label
                                        className="ct-form__label"
                                        htmlFor="ct-name"
                                    >
                                        Name *
                                    </label>
                                    <input
                                        id="ct-name"
                                        className="ct-form__input"
                                        name="name"
                                        type="text"
                                        autoComplete="name"
                                        placeholder="Jane Doe"
                                        required
                                    />
                                </div>
                                <div className="ct-form__field">
                                    <label
                                        className="ct-form__label"
                                        htmlFor="ct-email"
                                    >
                                        Email *
                                    </label>
                                    <input
                                        id="ct-email"
                                        className="ct-form__input"
                                        name="email"
                                        type="email"
                                        autoComplete="email"
                                        placeholder="you@example.com"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="ct-form__field">
                                <label
                                    className="ct-form__label"
                                    htmlFor="ct-subject"
                                >
                                    Subject *
                                </label>
                                <select
                                    id="ct-subject"
                                    className="ct-form__input ct-form__input--select"
                                    name="subject"
                                    required
                                >
                                    <option value="">Select a topic…</option>
                                    <option value="general">
                                        General enquiry
                                    </option>
                                    <option value="account">
                                        Account help
                                    </option>
                                    <option value="recording">
                                        Recording issue
                                    </option>
                                    <option value="billing">Billing</option>
                                    <option value="feedback">Feedback</option>
                                </select>
                            </div>

                            <div className="ct-form__field">
                                <label
                                    className="ct-form__label"
                                    htmlFor="ct-body"
                                >
                                    Message *
                                </label>
                                <textarea
                                    id="ct-body"
                                    className="ct-form__input ct-form__input--textarea"
                                    name="body"
                                    rows="5"
                                    placeholder="Share the details so we can help you faster."
                                    required
                                />
                            </div>

                            <div className="ct-form__footer">
                                <div className="ct-form__recaptcha">
                                    {/* <div data-netlify-recaptcha="true"></div> */}
                                    {captchaStatus === "loading" ? (
                                        <div
                                            className="ct-form__recaptcha-skeleton"
                                            aria-hidden="true"
                                        >
                                            <span className="ct-form__recaptcha-checkbox" />
                                            <span className="ct-form__recaptcha-lines" />
                                            <span className="ct-form__recaptcha-badge" />
                                        </div>
                                    ) : null}
                                    <div ref={recaptchaHostRef} />
                                </div>
                                {captchaStatus === "error" ? (
                                    <p className="ct-form__error" role="alert">
                                        CAPTCHA failed to load. Please refresh
                                        the page and try again.
                                    </p>
                                ) : null}
                                {error ? (
                                    <p className="ct-form__error" role="alert">
                                        {error}
                                    </p>
                                ) : null}
                                <button
                                    type="submit"
                                    className="ct-submit"
                                    disabled={
                                        submitting ||
                                        captchaStatus === "loading" ||
                                        !formIsComplete ||
                                        captchaStatus !== "verified"
                                    }
                                >
                                    Send message
                                    <span
                                        className="ct-submit__icon"
                                        aria-hidden="true"
                                    />
                                </button>
                                <p className="ct-form__hint">
                                    By submitting, you agree to be contacted by
                                    our support team.
                                </p>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}
